import {
  Badge,
  Box,
  Flex,
  Group,
  NumberFormatter,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import type { AllTimeOutstandingResult } from 'api/resource';
import { IconReceipt2 } from '@tabler/icons-react';
import { safeCastNumber } from 'utils/numberUtils';
import { formatDateOnly, nowISODate } from 'utils/dateUtils';
import { useProviderDashboardPalette } from './useProviderDashboardPalette';

export const AccountsReceivableDistributionCard = ({
  isLoading = false,
  outstandingSummaryData,
  isMobile,
  onOutstandingClick,
}: {
  isLoading?: boolean;
  outstandingSummaryData?: AllTimeOutstandingResult;
  isMobile: boolean;
  onOutstandingClick?: (payorType?: 'PUBLIC' | 'PRIVATE' | 'DIRECT') => void;
}) => {
  const { translate } = useTranslation();
  const palette = useProviderDashboardPalette();
  const {
    public: publicTotal = 0,
    'third-party': thirdPartyTotal = 0,
    direct: directTotal = 0,
    'all-time-start': allTimeStart,
  } = outstandingSummaryData ?? {};

  const totalOutstanding = safeCastNumber(
    publicTotal + thirdPartyTotal + directTotal
  );
  const distributionTotal = totalOutstanding || 1;

  const range = `${formatDateOnly(allTimeStart)} - ${formatDateOnly(
    nowISODate()
  )}`;

  return (
    <Paper
      p="lg"
      radius="lg"
      className="dashboard-chart"
      style={{
        background: palette.cardBackground,
        border: `1px solid ${palette.cardBorder}`,
      }}
    >
      {isLoading ? (
        <Stack
          gap="md"
          data-testid="provider-outstanding-distribution-skeleton"
        >
          <Skeleton height={20} width={220} />
          <Skeleton height={36} width={260} />
          <Skeleton height={18} width={180} />
          <Skeleton height={10} radius="xl" />
          <Flex gap="md" wrap="wrap" pt={4}>
            <Skeleton height={18} width={120} />
            <Skeleton height={18} width={120} />
            <Skeleton height={18} width={120} />
          </Flex>
        </Stack>
      ) : (
        <Stack gap="md">
          <Flex
            justify="space-between"
            align={isMobile ? 'flex-start' : 'center'}
            direction={isMobile ? 'column' : 'row'}
            gap="sm"
          >
            <Group gap="sm" align="center">
              <ThemeIcon
                size={34}
                radius="md"
                variant="filled"
                style={{
                  background: 'rgba(200, 84, 101, 0.12)',
                  color: '#cd636a',
                }}
              >
                <IconReceipt2 size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text size="sm" c={palette.subtleText}>
                  {translate(
                    'pages:providerDashboard.metrics.accountsReceivableCard.totalOutstanding',
                    'Total All-time Outstanding'
                  )}
                </Text>
                <UnstyledButton
                  onClick={() => onOutstandingClick?.()}
                  disabled={!onOutstandingClick}
                  aria-label={translate(
                    'pages:providerDashboard.metrics.accountsReceivableCard.totalOutstanding',
                    'Total All-time Outstanding'
                  )}
                >
                  <NumberFormatter
                    prefix="$"
                    value={totalOutstanding}
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    style={{
                      fontSize: isMobile ? 28 : 32,
                      fontWeight: 800,
                      color: palette.primaryText,
                    }}
                  />
                </UnstyledButton>
                <Badge
                  size="md"
                  radius="xl"
                  variant="filled"
                  style={{
                    background: 'rgba(200, 84, 101, 0.12)',
                    color: '#cd636a',
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {range}
                </Badge>
              </Stack>
            </Group>
          </Flex>

          <Stack gap={6}>
            <Text size="sm" c={palette.subtleText}>
              {translate(
                'pages:providerDashboard.metrics.accountsReceivableCard.distributionTitle',
                'Outstanding Distribution'
              )}
            </Text>
            <Group
              gap={0}
              wrap="nowrap"
              style={{
                width: '100%',
                overflow: 'hidden',
                borderRadius: 999,
              }}
            >
              <Box
                style={{
                  height: 10,
                  width: `${(publicTotal / distributionTotal) * 100}%`,
                  minWidth: publicTotal > 0 ? 10 : 0,
                  background: '#cd636a',
                }}
              />
              <Box
                style={{
                  height: 10,
                  width: `${(thirdPartyTotal / distributionTotal) * 100}%`,
                  minWidth: thirdPartyTotal > 0 ? 10 : 0,
                  background: '#4B96F1',
                }}
              />
              <Box
                style={{
                  height: 10,
                  width: `${(directTotal / distributionTotal) * 100}%`,
                  minWidth: directTotal > 0 ? 10 : 0,
                  background: '#F0B23A',
                }}
              />
            </Group>

            <Flex gap="md" wrap="wrap" pt={4}>
              {[
                [
                  translate(
                    'pages:providerDashboard.card.sections.publicPay',
                    'Public Pay'
                  ),
                  publicTotal,
                  '#cd636a',
                  'PUBLIC',
                ],
                [
                  translate(
                    'pages:providerDashboard.card.sections.thirdParty',
                    'Third-Party'
                  ),
                  thirdPartyTotal,
                  '#4B96F1',
                  'PRIVATE',
                ],
                [
                  translate(
                    'pages:providerDashboard.card.sections.directPay',
                    'Direct Pay'
                  ),
                  directTotal,
                  '#F0B23A',
                  'DIRECT',
                ],
              ].map(([label, value, color, payorType]) => (
                <UnstyledButton
                  key={label}
                  disabled={!onOutstandingClick}
                  onClick={() => {
                    if (
                      payorType === 'PUBLIC' ||
                      payorType === 'PRIVATE' ||
                      payorType === 'DIRECT'
                    )
                      onOutstandingClick?.(payorType);
                  }}
                >
                  <Group gap={6}>
                    <Box
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: `${color}`,
                      }}
                    />
                    <Text size="sm" c={palette.mutedText}>
                      {label}
                    </Text>
                    <NumberFormatter
                      prefix="$"
                      value={safeCastNumber(value as number)}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: palette.primaryText,
                      }}
                    />
                  </Group>
                </UnstyledButton>
              ))}
            </Flex>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};
